/**
 * 模块: KeywordTrendPivot Column Builder
 * 职责: 动态生成关键词趋势透视表列配置
 * 限制: <280行
 */

import React from 'react';
import { Space, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TrendIcon, TrendTag } from './components';
import { formatNumber, formatPercent } from './formatters';
import type { KeywordPivotData, PivotTableData } from './types';

/**
 * 生成动态列：每个周期一组列
 */
export const buildPivotColumns = (data: PivotTableData | null): ColumnsType<KeywordPivotData> => {
  console.log('[buildPivotColumns] 生成列:', { 
    hasData: !!data, 
    periodsLength: data?.periods?.length 
  });

  if (!data || data.periods.length === 0) {
    console.log('[buildPivotColumns] 数据为空，返回空列数组');
    return [];
  }

  const baseColumns: ColumnsType<KeywordPivotData> = [
    {
      title: '搜索词',
      dataIndex: 'search_query',
      key: 'search_query',
      fixed: 'left',
      width: 200,
      render: (text: string, record: KeywordPivotData) => (
        <Space direction="vertical" size={0}>
          <strong>{text}</strong>
          <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {record.total_periods} 个周期
          </span>
        </Space>
      ),
    },
    {
      title: '趋势',
      key: 'trend',
      width: 150,
      fixed: 'left',
      render: (_: any, record: KeywordPivotData) => (
        <Space direction="vertical" size={4}>
          <TrendTag trend={record.trend.direction} label="总体" />
          <Space size={4}>
            <Tooltip title="曝光趋势">
              <Tag style={{ fontSize: '12px' }}>
                展现 <TrendIcon trend={record.trend.impressions_trend} />
              </Tag>
            </Tooltip>
            <Tooltip title="点击趋势">
              <Tag style={{ fontSize: '12px' }}>
                点击 <TrendIcon trend={record.trend.clicks_trend} />
              </Tag>
            </Tooltip>
          </Space>
          <Tooltip title={`最新转化率: ${record.trend.latest_cvr}% | 平均: ${record.trend.avg_cvr}%`}>
            <Tag style={{ fontSize: '12px' }} color="blue">
              CVR <TrendIcon trend={record.trend.cvr_trend} />
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 为每个周期生成列组
  const periodColumns = data.periods.map((period) => ({
    title: (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 'bold' }}>{period.period_label}</div>
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
          {new Date(period.report_date).toLocaleDateString('zh-CN')}
        </div>
      </div>
    ),
    key: `period_${period.period_id}`,
    children: [
      {
        title: <Tooltip title="搜索量">搜索量</Tooltip>,
        key: `${period.period_id}_search_volume`,
        width: 90,
        render: (_: any, record: KeywordPivotData) => {
          const periodData = record.periods_data.find(
            (p) => p.period_id === period.period_id
          );
          return periodData && periodData.search_query_volume ? (
            <Tag color="purple">{formatNumber(periodData.search_query_volume)}</Tag>
          ) : (
            '-'
          );
        },
      },
      {
        title: <Tooltip title="大盘曝光量">展现量</Tooltip>,
        key: `${period.period_id}_impressions`,
        width: 100,
        render: (_: any, record: KeywordPivotData) => {
          const periodData = record.periods_data.find(
            (p) => p.period_id === period.period_id
          );
          return periodData ? (
            <div>
              <div>{formatNumber(periodData.impressions_total)}</div>
              <div style={{ fontSize: '12px', color: '#52c41a' }}>
                ASIN: {formatNumber(periodData.impressions_asin_count)} (
                {formatPercent(periodData.impressions_asin_share)})
              </div>
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        title: <Tooltip title="大盘点击量">点击量</Tooltip>,
        key: `${period.period_id}_clicks`,
        width: 100,
        render: (_: any, record: KeywordPivotData) => {
          const periodData = record.periods_data.find(
            (p) => p.period_id === period.period_id
          );
          return periodData ? (
            <div>
              <div>{formatNumber(periodData.clicks_total)}</div>
              <div style={{ fontSize: '12px', color: '#52c41a' }}>
                ASIN: {formatNumber(periodData.clicks_asin_count)} (
                {formatPercent(periodData.clicks_asin_share)})
              </div>
              <div style={{ fontSize: '12px', color: '#1890ff' }}>
                CTR: {formatPercent(periodData.ctr)}
              </div>
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        title: <Tooltip title="购买量">购买</Tooltip>,
        key: `${period.period_id}_purchases`,
        width: 100,
        render: (_: any, record: KeywordPivotData) => {
          const periodData = record.periods_data.find(
            (p) => p.period_id === period.period_id
          );
          return periodData ? (
            <div>
              <div>{formatNumber(periodData.purchases_total)}</div>
              <div style={{ fontSize: '12px', color: '#52c41a' }}>
                ASIN: {formatNumber(periodData.purchases_asin_count)} (
                {formatPercent(periodData.purchases_asin_share)})
              </div>
              <div style={{ fontSize: '12px', color: '#f5222d' }}>
                CVR: {formatPercent(periodData.cvr)}
              </div>
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        title: <Tooltip title="搜索查询分数">评分</Tooltip>,
        key: `${period.period_id}_score`,
        width: 80,
        render: (_: any, record: KeywordPivotData) => {
          const periodData = record.periods_data.find(
            (p) => p.period_id === period.period_id
          );
          return periodData ? (
            <Tag color={periodData.search_query_score >= 80 ? 'green' : 'orange'}>
              {periodData.search_query_score}
            </Tag>
          ) : (
            '-'
          );
        },
      },
    ],
  }));

  return [...baseColumns, ...periodColumns];
};

